import { useState, useEffect } from 'react';
import { useLocalization } from '../../contexts/LocalizationContext';
import brandService from '../../services/brandService';
import imageService from '../../services/imageService';
import Modal from '../../components/Common/Modal';
import Input from '../../components/Common/Input';
import Button from '../../components/Common/Button';

const emptyForm = {
  nameEn: '',
  nameAr: '',
  countryOfOrigin: '',
  website: '',
  descriptionEn: '',
  descriptionAr: '',
  logoUrl: '',
  isActive: true,
};

// Create when `brand` is null, edit otherwise.
const BrandDialog = ({ isOpen, onClose, onSuccess, brand }) => {
  const { get, isRtl } = useLocalization();
  const isEdit = !!brand;
  const [formData, setFormData] = useState(emptyForm);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!brand) return;
    setFormData({
      nameEn: brand.nameEn ?? '',
      nameAr: brand.nameAr ?? '',
      countryOfOrigin: brand.countryOfOrigin ?? '',
      website: brand.website ?? '',
      descriptionEn: brand.descriptionEn ?? '',
      descriptionAr: brand.descriptionAr ?? '',
      logoUrl: brand.logoUrl ?? '',
      isActive: brand.isActive ?? true,
    });
    setLogoPreview(brand.logoUrl ?? '');
  }, [brand]);

  useEffect(() => {
    if (!logoFile) return undefined;
    const url = URL.createObjectURL(logoFile);
    setLogoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [logoFile]);

  const set = (field) => (e) => setFormData({ ...formData, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      let logoUrl = formData.logoUrl;
      if (logoFile) {
        const upload = await imageService.uploadImages([logoFile], 'brands');
        if (!upload.success || !upload.imageUrls?.length) {
          throw new Error(upload.message || 'Logo upload failed');
        }
        logoUrl = upload.imageUrls[0];
      }

      const payload = { ...formData, logoUrl };
      if (isEdit) {
        await brandService.update(brand.id, payload);
      } else {
        await brandService.create(payload);
      }
      onSuccess();
    } catch (error) {
      console.error('Error saving brand:', error);
      const message = error.response?.data?.message || error.response?.data?.Message || error.message;
      alert(message || (isRtl ? 'حدث خطأ أثناء حفظ العلامة التجارية' : 'Error saving brand'));
    } finally {
      setIsLoading(false);
    }
  };

  const clearLogo = () => {
    setLogoFile(null);
    setLogoPreview('');
    setFormData({ ...formData, logoUrl: '' });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? (isRtl ? 'تعديل العلامة التجارية' : 'Edit Brand') : get('add_new_brand')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {get('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={isLoading}>
            {get('save')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">{isRtl ? 'الشعار' : 'Logo'}</label>
          {logoPreview && (
            <div style={{ marginBottom: 'var(--sp-2)' }}>
              <img
                src={logoPreview}
                alt=""
                style={{ maxHeight: 80, maxWidth: 200, objectFit: 'contain', background: '#fff', border: '1px solid var(--border)', borderRadius: 8, padding: 4 }}
              />
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            className="form-input"
            onChange={(e) => setLogoFile(e.target.files?.[0] ?? null)}
          />
          {logoPreview && (
            <div style={{ marginTop: 'var(--sp-2)' }}>
              <Button size="small" variant="secondary" onClick={clearLogo}>
                {isRtl ? 'إزالة الشعار' : 'Remove logo'}
              </Button>
            </div>
          )}
        </div>
        <Input
          label={isRtl ? 'الاسم (إنجليزي)' : 'Name (English)'}
          value={formData.nameEn}
          onChange={set('nameEn')}
          required
        />
        <Input
          label={isRtl ? 'الاسم (عربي)' : 'Name (Arabic)'}
          value={formData.nameAr}
          onChange={set('nameAr')}
          required
        />
        <Input
          label={isRtl ? 'بلد المنشأ' : 'Country of origin'}
          value={formData.countryOfOrigin}
          onChange={set('countryOfOrigin')}
        />
        <Input
          label={isRtl ? 'الموقع الإلكتروني' : 'Website'}
          type="url"
          value={formData.website}
          onChange={set('website')}
        />
        <div className="form-group">
          <label className="form-label">{isRtl ? 'الوصف (إنجليزي)' : 'Description (English)'}</label>
          <textarea className="form-textarea" value={formData.descriptionEn} onChange={set('descriptionEn')} rows="3" />
        </div>
        <div className="form-group">
          <label className="form-label">{isRtl ? 'الوصف (عربي)' : 'Description (Arabic)'}</label>
          <textarea className="form-textarea" value={formData.descriptionAr} onChange={set('descriptionAr')} rows="3" />
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <input
              type="checkbox"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
            />
            {isRtl ? 'نشط (يظهر في المتجر)' : 'Active (shown in the store)'}
          </label>
        </div>
      </form>
    </Modal>
  );
};

export default BrandDialog;
